/** Why a manual port rotation reorder cannot go ahead.
 * spec: specs/features/port-rotation.md */
export enum PortCallSwapRefusal {
  /** The move would go past the first or last position of the rotation. */
  RotationEnd = 'RotationEnd',
  /** The neighbouring location holds no port call to trade places with. */
  NoAdjacentPortCall = 'NoAdjacentPortCall',
  /** An off-hire holds the port call's own location or the neighbouring one. */
  OffHireAtPortCall = 'OffHireAtPortCall',
  /** Both port calls handle the same order, so swapping would invert its load and discharge. */
  SameOrderInBothPortCalls = 'SameOrderInBothPortCalls',
}

export class PortCallSwapError extends Error {
  constructor(public readonly refusal: PortCallSwapRefusal) {
    super(`port call swap is not valid: ${refusal}`);
    this.name = 'PortCallSwapError';
  }
}

const REFUSAL_MESSAGES: Record<PortCallSwapRefusal, string> = {
  [PortCallSwapRefusal.RotationEnd]:
    'Could not move the port call: it is already at that end of the rotation.',
  [PortCallSwapRefusal.NoAdjacentPortCall]:
    'Could not move the port call: the neighbouring position is a TC delivery, which cannot change places with a port call.',
  [PortCallSwapRefusal.OffHireAtPortCall]:
    'Could not move the port call: an off-hire at this or the neighbouring port call cannot change places in the rotation.',
  [PortCallSwapRefusal.SameOrderInBothPortCalls]:
    'Could not move the port call: the neighbouring port call handles the same order.',
};

/** The remaining throws on this path are internal assertions whose messages embed client ids,
 * so anything but a refusal gets generic copy and is left to `console.error`. */
export const getPortCallSwapFailureMessage = (error: unknown): string =>
  error instanceof PortCallSwapError
    ? REFUSAL_MESSAGES[error.refusal]
    : 'Could not change the port rotation. Please try again.';
